import React from "react"
import { Footer } from "./Footer"
import { Grid, Dropdown } from "semantic-ui-react"
import { FormContainer, Worksheet, LabeledInput, DatePicker } from "uikit"
import ConnectedAccountBar from "../../ConnectedAccountBar"
import HumanTimeSince from "components/HumanTimeSince"
import { useStore } from "store"

const ESPRLoanAccountForm = ({
  data: {
    fields: { account_id, balance, interest_rate, maturity_date },
    isModified,
    onSubmit,
    isPending,
    hasError,
    error
  },
  submitLabel = "Done",
  submitHeader = "",
  isNew,
  onDelete,
  accountOptions,
  lastUpdatedAt,
  account
}) => {
  return (
    <Grid>
      {account.connection_id ? (
        <Grid.Row style={{ borderBottom: "1px solid #ccc" }}>
          <Grid.Column>
            <ConnectedAccountBar {...account} />
          </Grid.Column>
        </Grid.Row>
      ) : null}
      <Grid.Row>
        <Grid.Column>
          <Worksheet error={error} style={{ borderTop: "1px solid #ccc" }}>
            <Worksheet.Body>
              <Worksheet.Row
                label="What account was this loan taken out of?"
                paddedField
              >
                <Worksheet.Field error={account_id.error}>
                  <Dropdown
                    {...account_id}
                    placeholder="Account"
                    options={accountOptions}
                    selection
                  />
                </Worksheet.Field>
              </Worksheet.Row>
              <Worksheet.Row
                label="What is the current balance of your loan?"
                description={
                  account.connection_id
                    ? "Automatically updated through the account connection"
                    : null
                }
                paddedField
              >
                <Worksheet.Field error={balance.error}>
                  <LabeledInput
                    {...balance}
                    label=""
                    iconText="$"
                    iconPosition="left"
                    placeholder="0"
                    disabled={!!account.connection_id}
                    data-testid="balance"
                  />
                  {lastUpdatedAt}
                </Worksheet.Field>
              </Worksheet.Row>
              <Worksheet.Row label="What is the interest rate?" paddedField>
                <Worksheet.Field error={interest_rate.error} padded>
                  <LabeledInput
                    {...interest_rate}
                    label=""
                    iconText="%"
                    placeholder="0"
                    data-testid="interest_rate"
                  />
                </Worksheet.Field>
              </Worksheet.Row>
              <Worksheet.Row
                label="When must the loan be paid back?"
                paddedField
              >
                <Worksheet.Field error={maturity_date.error} padded>
                  <DatePicker {...maturity_date} />
                </Worksheet.Field>
              </Worksheet.Row>
            </Worksheet.Body>
            <Footer
              isNew={isNew}
              submitLabel={submitLabel}
              onSubmit={onSubmit}
              onDelete={onDelete}
              isModified={isModified}
              deleteLabel={account.connection_id ? "Ignore Account" : "Delete"}
              hasConnectionId={account.connection_id ? true : false}
              error={error}
            />
          </Worksheet>{" "}
        </Grid.Column>
      </Grid.Row>
    </Grid>
  )
}

const ESPRLoanAccountFormContainer = FormContainer(
  {
    account_id: {
      type: "string",
      required: true
    },
    balance: {
      type: "unit",
      required: true,
      negative: true
    },
    interest_rate: {
      type: "decimal",
      required: true,
      minValue: 0,
      maxValue: 100
    },
    maturity_date: { type: "date", required: true }
  },
  ({ fields: { maturity_date } }, ...form) => {
    if (
      !maturity_date ||
      !maturity_date.value ||
      parseInt(maturity_date.value.split("/").slice(-1)[0], 10) <
        new Date().getFullYear()
    ) {
      return {
        fields: {
          maturity_date: "Loan must have a future payback date."
        }
      }
    }
  }
)(ESPRLoanAccountForm)

const ESPRLoanAccount = ({ household, account, ...formProps }) => {
  const isNew = account === null
  let values = isNew ? {} : account
  const lastUpdatedAt = !isNew ? (
    <p
      style={{
        fontSize: "0.8em",
        textAlign: "right",
        fontWeight: "normal"
      }}
    >
      Updated <HumanTimeSince time={values.balance_updated_at} /> ago
    </p>
  ) : null
  const [accounts] = useStore("householdAccounts", household.id)
  let accountOptions = Object.values(accounts)
    .filter(({ type }) =>
      ["401k", "403b", "457b", "roth401k", "roth403b", "roth457b"].includes(
        type
      )
    )
    .map(({ id: value, name: text }) => ({
      value,
      text
    }))
  return (
    <ESPRLoanAccountFormContainer
      {...formProps}
      values={values}
      accountOptions={accountOptions}
      lastUpdatedAt={lastUpdatedAt}
      account={values}
    />
  )
}

export default ESPRLoanAccount
